import React, { useEffect, useState } from 'react';
import AppErrorBoundary from './components/AppErrorBoundary';
import ClientConsolidationPanel from './components/ClientConsolidationPanel';
import LoginScreen from './components/LoginScreen';
import ProductConsolidationPanel from './components/ProductConsolidationPanel';
import ServiceCostCentersPanel from './components/ServiceCostCentersPanel';
import SyncCenterPanel from './components/SyncCenterPanel';
import SystemLogBar from './components/SystemLogBar';
import ClientIntegrationConciliation from './pages/ClientIntegrationConciliation';
import Tarefas from './pages/Tarefas';
import WhatsAppAtendimentos from './pages/WhatsAppAtendimentos';
import { AuthAPI } from './services/auth';
import { clearStoredSession, persistSession, readStoredSession } from './services/session';
import './App.css';

const NAV_ITEMS = [
  { path: '/', label: 'Tarefas', description: 'Fila e rotina' },
  { path: '/service-cost-centers', label: 'Propostas', description: 'Compras para venda' },
  { path: '/client-consolidation', label: 'Clientes', description: 'Cadastro mestre' },
  { path: '/clientes/conciliacao', label: 'Conciliação', description: 'Bling e Auvo' },
  { path: '/product-consolidation', label: 'Produtos', description: 'Cadastro mestre' },
  { path: '/whatsapp', label: 'WhatsApp', description: 'Atendimentos' },
  { path: '/sync-center', label: 'Sincronização', description: 'Integrações' }
];

export default function App() {
  const [session, setSession] = useState(() => readStoredSession());
  const [bootstrapping, setBootstrapping] = useState(() => Boolean(readStoredSession()?.token));
  const [path, setPath] = useState(() => window.location.pathname);
  const [authNotice, setAuthNotice] = useState('');

  useEffect(() => {
    let cancelled = false;

    const restoreSession = async () => {
      const storedSession = readStoredSession();
      if (!storedSession?.token) {
        if (!cancelled) setBootstrapping(false);
        return;
      }

      try {
        const { user } = await AuthAPI.me();
        const nextSession = { ...storedSession, user };

        persistSession(nextSession);
        if (!cancelled) setSession(nextSession);
      } catch (_error) {
        clearStoredSession();
        if (!cancelled) setSession(null);
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    };

    restoreSession();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleNavigation = () => setPath(window.location.pathname);
    window.addEventListener('popstate', handleNavigation);
    return () => window.removeEventListener('popstate', handleNavigation);
  }, []);

  useEffect(() => {
    const handleExpiredSession = () => {
      clearStoredSession();
      setSession(null);
      setBootstrapping(false);
      setAuthNotice('Sua sessão expirou. Faça login novamente para continuar.');
    };

    window.addEventListener('task-hub:session-expired', handleExpiredSession);
    return () => window.removeEventListener('task-hub:session-expired', handleExpiredSession);
  }, []);

  const handleLogin = (nextSession) => {
    setAuthNotice('');
    setSession(nextSession);
    persistSession(nextSession);
  };

  const handleLogout = () => {
    setAuthNotice('');
    setSession(null);
    clearStoredSession();
  };

  const handleSessionUserUpdated = (nextUser) => {
    setSession((current) => {
      if (!current?.token) return current;
      const nextSession = { ...current, user: nextUser };
      persistSession(nextSession);
      return nextSession;
    });
  };

  const navigate = (nextPath) => {
    if (window.location.pathname === nextPath) return;
    window.history.pushState({}, '', nextPath);
    setPath(nextPath);
  };

  const isActiveRoute = (itemPath) => {
    if (itemPath === '/') return path === '/';
    return path === itemPath || path.startsWith(`${itemPath}/`);
  };

  const renderAppChrome = (content) => (
    <>
      <header className="top-nav-shell">
        <div className="top-nav-brand">
          <div className="eyebrow">Task Hub</div>
          <strong>Câmara & Cia</strong>
        </div>
        <nav className="top-nav-menu" aria-label="Ferramentas principais">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.path}
              type="button"
              className={`top-nav-item ${isActiveRoute(item.path) ? 'top-nav-item--active' : ''}`}
              onClick={() => navigate(item.path)}
            >
              <span>{item.label}</span>
              <small>{item.description}</small>
            </button>
          ))}
        </nav>
        <div className="top-nav-user">
          <span>{session.user.name || session.user.email || session.user.code}</span>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleLogout}>Sair</button>
        </div>
      </header>
      {content}
    </>
  );

  const renderWithSystemLog = (content) => (
    <>
      <AppErrorBoundary>{content}</AppErrorBoundary>
      <SystemLogBar />
    </>
  );

  if (bootstrapping) {
    return renderWithSystemLog(
      <div className="login-shell">
        <div className="login-card shadow-lg">
          <div className="eyebrow">Task Hub</div>
          <h1>Restaurando sessão</h1>
          <p className="text-muted mb-0">Validando o acesso antes de abrir o painel.</p>
        </div>
      </div>
    );
  }

  if (!session?.user) {
    return renderWithSystemLog(<LoginScreen onLogin={handleLogin} notice={authNotice} />);
  }

  if (path === '/client-consolidation') {
    return renderWithSystemLog(
      renderAppChrome(
        <ClientConsolidationPanel
          pageMode
          currentUser={session.user}
          onClose={() => navigate('/')}
          onUnauthorized={handleLogout}
        />
      )
    );
  }

  if (path === '/clientes/conciliacao') {
    return renderWithSystemLog(
      renderAppChrome(
        <ClientIntegrationConciliation onUnauthorized={handleLogout} />
      )
    );
  }

  if (path === '/product-consolidation') {
    return renderWithSystemLog(
      renderAppChrome(
        <ProductConsolidationPanel
          pageMode
          currentUser={session.user}
          onClose={() => navigate('/')}
          onUnauthorized={handleLogout}
        />
      )
    );
  }

  if (path === '/service-cost-centers' || path.startsWith('/service-cost-centers/')) {
    return renderWithSystemLog(
      renderAppChrome(
        <ServiceCostCentersPanel
          pageMode
          currentUser={session.user}
          onClose={() => navigate('/')}
          onUnauthorized={handleLogout}
        />
      )
    );
  }

  if (path === '/sync-center') {
    return renderWithSystemLog(
      renderAppChrome(
        <SyncCenterPanel
          pageMode
          onClose={() => navigate('/')}
          onUnauthorized={handleLogout}
        />
      )
    );
  }

  if (path === '/whatsapp') {
    return renderWithSystemLog(
      renderAppChrome(
        <WhatsAppAtendimentos currentUser={session.user} onUnauthorized={handleLogout} />
      )
    );
  }

  return renderWithSystemLog(
    renderAppChrome(
      <Tarefas
        currentUser={session.user}
        onLogout={handleLogout}
        onSessionUserUpdated={handleSessionUserUpdated}
      />
    )
  );
}
